import { useEffect, useRef, useState } from 'react'
import { useAuth } from '@it-sce/ui/contexts/KeycloakAuthContext'
import { useToast } from '../components/ui'
import {
  isTqCancelUnavailable, isTqNotEnabled, TQ_CANCEL_SUPPORTED,
  MSG_TQ_CANCEL_UNAVAILABLE, MSG_TQ_NOT_ENABLED,
} from '../lib/tqErrors'

interface Organization {
  id: number
  name: string
  slug: string
  public_token?: string
  member_role: string
}

interface Task {
  id: number
  title: string
  description?: string
  status: string
  created_at: string
  estimated_wait?: string
  result?: string
  tokens_used?: number
  metadata?: {
    tokens?: number
    customer_email?: string
    [key: string]: unknown
  }
}

interface Props {
  organization: Organization
}

function statusColor(status: string): string {
  switch (status) {
    case 'queued': return '#666'
    case 'in_progress': return '#1a5bbb'
    case 'done': return '#1a7340'
    case 'failed': return '#b91c1c'
    case 'cancelled': return '#888'
    default: return '#666'
  }
}

function statusBg(status: string): string {
  switch (status) {
    case 'queued': return '#f3f4f6'
    case 'in_progress': return '#dbeafe'
    case 'done': return '#e6f4ea'
    case 'failed': return '#fee2e2'
    case 'cancelled': return '#f3f4f6'
    default: return '#f3f4f6'
  }
}

function statusLabel(status: string): string {
  switch (status) {
    case 'queued': return 'Warteschlange'
    case 'in_progress': return 'In Bearbeitung'
    case 'done': return 'Abgeschlossen'
    case 'failed': return 'Fehlgeschlagen'
    case 'cancelled': return 'Abgebrochen'
    default: return status
  }
}

function Badge({ label, bg, color }: { label: string; bg: string; color: string }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 12,
      fontSize: 12, fontWeight: 600, background: bg, color,
    }}>{label}</span>
  )
}

function formatDate(ts: string): string {
  if (!ts) return '—'
  try {
    return new Date(ts).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
  } catch {
    return ts
  }
}

function getTokenCount(task: Task): number | null {
  if (task.tokens_used != null) return task.tokens_used
  if (task.metadata?.tokens != null) return task.metadata.tokens as number
  return null
}

export default function Tasks({ organization }: Props) {
  const { showToast } = useToast()
  const { api: keycloakApi } = useAuth()
  const [tasks, setTasks] = useState<Task[]>([])
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState<Set<number>>(new Set())
  const [creating, setCreating] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [cancelling, setCancelling] = useState<number | null>(null)
  const [notEnabled, setNotEnabled] = useState(false)
  const [cancelUnavailable, setCancelUnavailable] = useState(false)
  const [form, setForm] = useState({ title: '', description: '' })

  const slug = organization.public_token || organization.slug

  // Request-Race (TQ #729): eine verspaetete Antwort der alten Org darf die Liste der neuen nicht ueberschreiben.
  const currentSlug = useRef(slug)
  currentSlug.current = slug
  const latestLoad = useRef(0)

  useEffect(() => {
    // Organisationswechsel: Zustand der alten Org verwerfen, sonst bleibt deren Meldung stehen.
    setNotEnabled(false)
    setCancelUnavailable(false)
    setTasks([])
    setCreating(false)
    setExpanded(new Set())
    loadTasks()
  }, [organization.slug, slug])

  const loadTasks = async () => {
    const requestSlug = slug
    // Aufruf aus einem Handler der alten Org (nach Wechsel): nichts laden, nichts ueberschreiben.
    if (requestSlug !== currentSlug.current) return
    const requestId = ++latestLoad.current
    const isStale = () => requestId !== latestLoad.current || requestSlug !== currentSlug.current
    try {
      setLoading(true)
      const response = await keycloakApi!.get(`/api/org/${requestSlug}/tasks?limit=50`)
      if (isStale()) return
      const raw = response.data.tasks ?? []
      setTasks(Array.isArray(raw) ? raw : [])
      setNotEnabled(false)
    } catch (err) {
      if (isStale()) return
      if (isTqNotEnabled(err)) {
        // Kein Fehler-Toast und kein Leerzustand: die Organisation ist (noch) nicht freigeschaltet.
        setTasks([])
        setNotEnabled(true)
      } else {
        showToast('error', 'Aufträge konnten nicht geladen werden')
      }
    } finally {
      if (!isStale()) setLoading(false)
    }
  }

  const toggleExpand = (id: number) => {
    setExpanded(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const submitTask = async () => {
    if (!form.title.trim()) return
    setSubmitting(true)
    try {
      await keycloakApi!.post(`/api/org/${slug}/tasks`, {
        title: form.title,
        description: form.description,
      })
      showToast('success', 'Auftrag wurde eingereicht')
      setCreating(false)
      setForm({ title: '', description: '' })
      await loadTasks()
    } catch (err) {
      if (isTqNotEnabled(err)) {
        setNotEnabled(true)
        setCreating(false)
        showToast('info', MSG_TQ_NOT_ENABLED)
      } else {
        showToast('error', 'Auftrag konnte nicht erstellt werden')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const cancelTask = async (id: number) => {
    setCancelling(id)
    try {
      await keycloakApi!.delete(`/api/org/${slug}/tasks/${id}`)
      showToast('success', `Auftrag #${id} abgebrochen`)
      await loadTasks()
    } catch (err) {
      if (isTqCancelUnavailable(err)) {
        setCancelUnavailable(true)
        showToast('info', MSG_TQ_CANCEL_UNAVAILABLE)
      } else if (isTqNotEnabled(err)) {
        setNotEnabled(true)
        showToast('info', MSG_TQ_NOT_ENABLED)
      } else {
        showToast('error', 'Auftrag konnte nicht abgebrochen werden')
      }
    } finally {
      setCancelling(null)
    }
  }

  const activeCount = tasks.filter(t => t.status === 'in_progress' || t.status === 'queued').length

  return (
    <div style={{ padding: '32px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div>
          <h2 style={{ margin: 0 }}>KI-Aufträge</h2>
          {!loading && tasks.length > 0 && (
            <p style={{ margin: '4px 0 0', color: '#888', fontSize: 14 }}>
              {activeCount > 0 ? `${activeCount} aktiv · ` : ''}{tasks.length} gesamt
            </p>
          )}
        </div>
        {!notEnabled && <button
          onClick={() => { setCreating(true); setForm({ title: '', description: '' }) }}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '9px 18px', borderRadius: 8, border: 'none',
            background: creating ? '#3730a3' : '#4f46e5', color: '#fff',
            cursor: 'pointer', fontSize: 13, fontWeight: 600,
          }}
        >
          + Neuer Auftrag
        </button>}
      </div>

      {creating && !notEnabled && (
        <div style={{
          background: '#f8f8fc', border: '1px solid #e0e0f0', borderRadius: 12,
          padding: '20px', marginBottom: 24,
        }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 16 }}>Neuen KI-Auftrag einreichen</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <input
              placeholder="Titel des Auftrags *"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              style={{
                padding: '10px 12px', borderRadius: 8, border: '1px solid #ddd',
                fontSize: 14, outline: 'none',
              }}
            />
            <textarea
              placeholder="Beschreibung (optional) — was soll die KI tun?"
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              rows={4}
              style={{
                padding: '10px 12px', borderRadius: 8, border: '1px solid #ddd',
                fontSize: 14, resize: 'vertical', outline: 'none',
              }}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setCreating(false)}
                style={{
                  padding: '8px 16px', borderRadius: 8, border: '1px solid #ddd',
                  background: '#fff', cursor: 'pointer', fontSize: 13,
                }}
              >Abbrechen</button>
              <button
                onClick={submitTask}
                disabled={submitting || !form.title.trim()}
                style={{
                  padding: '8px 16px', borderRadius: 8, border: 'none',
                  background: submitting || !form.title.trim() ? '#aaa' : '#4f46e5', color: '#fff',
                  cursor: submitting || !form.title.trim() ? 'not-allowed' : 'pointer',
                  fontSize: 13, fontWeight: 600,
                }}
              >{submitting ? 'Wird eingereicht…' : 'Auftrag einreichen'}</button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <p style={{ color: '#888' }}>Aufträge werden geladen…</p>
      ) : notEnabled ? (
        <div role="status" style={{
          textAlign: 'center', padding: '40px 32px', background: '#fafafa',
          borderRadius: 12, border: '1px solid #e5e7eb', maxWidth: 640, margin: '0 auto',
          color: '#555', fontSize: 14, lineHeight: 1.55,
        }}>{MSG_TQ_NOT_ENABLED}</div>
      ) : tasks.length === 0 && !creating ? (
        <div style={{
          textAlign: 'center', padding: '56px 32px',
          background: '#fafafa', borderRadius: 12, border: '1px dashed #ddd',
          maxWidth: 640, margin: '0 auto',
        }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🤖</div>
          <h3 style={{ margin: '0 0 12px', fontSize: 20, color: '#333' }}>Noch keine KI-Aufträge</h3>
          <p style={{ color: '#555', margin: '0 auto 8px', fontSize: 14, lineHeight: 1.55, maxWidth: 480 }}>
            KI-Aufträge sind klar umrissene Aufgaben, die Aria im Hintergrund erledigt —
            z.B. eine Mail-Auswertung, eine kurze Recherche oder das Vorbereiten eines
            Angebots.
          </p>
          <p style={{ color: '#555', margin: '0 auto 24px', fontSize: 14, lineHeight: 1.55, maxWidth: 480 }}>
            Du beschreibst was getan werden soll — Aria zieht die nötigen Daten zusammen
            und meldet sich mit dem Ergebnis zurück.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 20 }}>
            <button
              onClick={() => {
                window.dispatchEvent(new CustomEvent('aria:open', {
                  detail: { prefill: 'Ich möchte einen KI-Auftrag anlegen für: ' },
                }))
              }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '10px 18px', borderRadius: 8, border: '1px solid #4f46e5',
                background: '#fff', color: '#4f46e5',
                cursor: 'pointer', fontSize: 13, fontWeight: 600,
              }}
            >💬 Mit Aria besprechen</button>
            <button
              onClick={() => { setCreating(true); setForm({ title: '', description: '' }) }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '10px 18px', borderRadius: 8, border: 'none',
                background: '#4f46e5', color: '#fff',
                cursor: 'pointer', fontSize: 13, fontWeight: 600,
              }}
            >+ Auftrag direkt anlegen</button>
          </div>
          <p style={{ color: '#9ca3af', fontSize: 12, margin: 0 }}>
            Mehr Beispiele und Hinweise:{' '}
            <a
              href="https://docs.it-sce.de/workspace/ki-auftraege"
              target="_blank"
              rel="noreferrer"
              style={{ color: '#4f46e5', textDecoration: 'underline' }}
            >Doku zum WORKSPACE-TQ Modul</a>
          </p>
        </div>
      ) : tasks.length === 0 ? null : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {tasks.map(task => {
            const isOpen = expanded.has(task.id)
            const isDone = task.status === 'done'
            const canCancel = TQ_CANCEL_SUPPORTED && task.status === 'queued' && !cancelUnavailable
            const tokens = getTokenCount(task)
            return (
              <div key={task.id} style={{
                border: '1px solid #eee', borderRadius: 10, overflow: 'hidden',
              }}>
                <div
                  onClick={() => toggleExpand(task.id)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '14px 16px', cursor: 'pointer',
                    background: isOpen ? '#f5f5ff' : '#fff',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                    <span style={{ color: '#aaa', fontSize: 12, minWidth: 48, flexShrink: 0 }}>#{task.id}</span>
                    <span style={{ fontSize: 14, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {task.title}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    <Badge label={statusLabel(task.status)} bg={statusBg(task.status)} color={statusColor(task.status)} />
                    {tokens != null && (
                      <span style={{ color: '#9ca3af', fontSize: 12 }}>{tokens.toLocaleString('de-DE')} Token</span>
                    )}
                    <span style={{ color: '#bbb', fontSize: 12 }}>{formatDate(task.created_at)}</span>
                    <span style={{
                      color: '#9ca3af', transition: 'transform 0.2s',
                      transform: isOpen ? 'rotate(90deg)' : 'none', display: 'inline-block', lineHeight: 1,
                    }}>›</span>
                  </div>
                </div>
                {isOpen && (
                  <div style={{ padding: '16px', borderTop: '1px solid #eee', background: '#fafafa', fontSize: 14 }}>
                    {task.description && (
                      <p style={{ color: '#555', margin: '0 0 12px' }}>{task.description}</p>
                    )}
                    <div style={{ display: 'flex', gap: 24, color: '#666', marginBottom: 12, flexWrap: 'wrap' }}>
                      <span><strong>Status:</strong> {statusLabel(task.status)}</span>
                      {task.estimated_wait && task.status === 'queued' && (
                        <span><strong>Geschätzte Wartezeit:</strong> {task.estimated_wait}</span>
                      )}
                      {tokens != null && (
                        <span><strong>Token-Verbrauch:</strong> {tokens.toLocaleString('de-DE')}</span>
                      )}
                    </div>
                    {isDone && task.result && (
                      <div style={{
                        background: '#fff', border: '1px solid #e0e0f0', borderRadius: 8,
                        padding: '12px 14px', marginBottom: 12,
                      }}>
                        <strong style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 8 }}>ERGEBNIS</strong>
                        <pre style={{
                          margin: 0, fontSize: 13, color: '#333', whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word', fontFamily: 'inherit',
                        }}>{task.result}</pre>
                      </div>
                    )}
                    {isDone && !task.result && (
                      <p style={{ color: '#888', margin: '0 0 12px', fontStyle: 'italic' }}>
                        Ergebnis wird nachgeladen…
                      </p>
                    )}
                    {canCancel && (
                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <button
                          onClick={e => { e.stopPropagation(); cancelTask(task.id) }}
                          disabled={cancelling === task.id}
                          style={{
                            padding: '6px 14px', borderRadius: 8,
                            border: '1px solid #fca5a5', background: '#fff5f5',
                            color: '#dc2626', cursor: cancelling === task.id ? 'not-allowed' : 'pointer',
                            fontSize: 12, fontWeight: 600,
                          }}
                        >{cancelling === task.id ? 'Wird abgebrochen…' : 'Auftrag abbrechen'}</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
